import React, { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme } from '@mui/material/styles';

export const ThemeContext = createContext();

export const ThemeContextProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'light'
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            light: '#e74c3c',
            main: '#dc3545',  // Red from Safety24 logo
            dark: '#c82333',
            contrastText: '#ffffff',
          },
          secondary: {
            light: '#5a8fd8',
            main: '#1e3a8a',  // Blue from Safety24 logo
            dark: '#0f2557',
            contrastText: '#ffffff',
          },
          info: {
            main: '#2563eb',
            light: '#60a5fa',
            dark: '#1e40af',
          },
          ...(mode === 'light'
            ? {
                // Light mode
                background: {
                  default: '#f8f9fa',
                  paper: '#ffffff',
                },
                text: {
                  primary: '#1e293b',
                  secondary: '#64748b',
                },
              }
            : {
                // Dark mode
                background: {
                  default: '#0f172a',
                  paper: '#1e293b',
                },
                text: {
                  primary: '#f1f5f9',
                  secondary: '#cbd5e1',
                },
              }),
        },
        shape: {
          borderRadius: 12,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 8,
              },
              contained: {
                boxShadow: '0 2px 8px rgba(220, 53, 69, 0.25)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(220, 53, 69, 0.35)',
                },
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: mode === 'light' 
                  ? '0 2px 12px rgba(0, 0, 0, 0.08)'
                  : '0 4px 16px rgba(0, 0, 0, 0.4)',
                '&:hover': {
                  boxShadow: mode === 'light'
                    ? '0 4px 16px rgba(0, 0, 0, 0.12)'
                    : '0 6px 20px rgba(0, 0, 0, 0.5)',
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow: mode === 'light'
                  ? '0 2px 8px rgba(0, 0, 0, 0.1)'
                  : '0 2px 12px rgba(0, 0, 0, 0.3)',
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderRight: mode === 'light' 
                  ? '1px solid #e2e8f0'
                  : '1px solid #334155',
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};
